import { isVideoModelId, type VideoModelId } from '@famecreator/stories-core';

import type { PartKind } from '../api/ads.client';

/**
 * The generator's unsent prompt, video model, length and count per project and kind, kept for the browser tab
 * (sessionStorage, as the playground's prompt draft). Logout clears them, so the next person to
 * sign in on the same tab never sees another user's prompts.
 */
export type Draft = { prompt: string; model: VideoModelId; duration: number; count: number };

const PREFIX = 'ads-draft:';
const key = (projectId: string, kind: PartKind) => `${PREFIX}${projectId}:${kind}`;

export function readDraft(projectId: string, kind: PartKind, fallback: Draft): Draft {
  try {
    const raw = sessionStorage.getItem(key(projectId, kind));
    const saved = (raw ? JSON.parse(raw) : {}) as Partial<Record<keyof Draft, unknown>>;
    return {
      prompt: typeof saved.prompt === 'string' ? saved.prompt : fallback.prompt,
      model: isVideoModelId(saved.model) ? saved.model : fallback.model,
      duration: typeof saved.duration === 'number' ? saved.duration : fallback.duration,
      count: typeof saved.count === 'number' ? saved.count : fallback.count,
    };
  } catch {
    return fallback;
  }
}

export function writeDraft(projectId: string, kind: PartKind, draft: Draft): void {
  try {
    sessionStorage.setItem(key(projectId, kind), JSON.stringify(draft));
  } catch {
    // Storage refused (private window): the draft lives only while the panel is open.
  }
}

export function clearAdsDrafts(): void {
  try {
    for (const k of Object.keys(sessionStorage)) if (k.startsWith(PREFIX)) sessionStorage.removeItem(k);
  } catch {
    // Nothing readable to clear.
  }
}
